import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';

// Redux
import { useSelector } from 'react-redux';

// Draggable Scroll
import { useDraggable } from 'react-use-draggable-scroll';

// Material
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';

// Helpers
import { Order, stableSort, getComparator } from 'helpers/tableSortLabels';
import { currencyFormat } from 'helpers/currencyFormat';

// Components

// Constants
import CONST_PAYMENT from 'constants/paymentsReceived';

// Types
import { PaymentInterface } from 'store/ducks/reports/types';
import { ApplicationState } from 'store';

// Styles
import useStyle from './tablePayments.styles';

const TotalCountComponent = lazy(() =>
  import('components/TotalCountComponent'),
);

interface TablePaymentsProps {
  periodString?: string;
  isHistoryDatails?: boolean;
  paymentsList?: PaymentInterface[];
  handleSetSimplePdfData?: (data: any) => void;
}

type Props = TablePaymentsProps;

const TablePayments: React.FC<Props> = ({
  periodString,
  isHistoryDatails,
  paymentsList,
  handleSetSimplePdfData = () => {},
}) => {
  const classes = useStyle();
  const parentRef: any = useRef();
  const { events } = useDraggable(parentRef);

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('amount');

  const initialPaymentsList = useSelector(
    (state: ApplicationState) => paymentsList || state.reports.paymentList,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
    const headerSimplePdf = ['Valor', 'Data e Hora', 'Tipo', 'De', 'Para'];

    const rowsSimplePdf =
      initialPaymentsList.length > 0
        ? stableSort(
            initialPaymentsList,
            getComparator(order, orderBy),
          ).map((payment: PaymentInterface) => [
            typeof payment.value === 'number'
              ? `${currencyFormat({ value: payment.value || 0 })}`
              : '-',
            new Date(payment?.date).getTime()
              ? new Date(payment.date).toLocaleString('pt-BR')
              : '-',
            payment?.type
              ? CONST_PAYMENT.TYPES.find(({ KEY }) => KEY === payment?.type)
                  ?.VALUE
              : '---',
            payment?.from ? payment?.from : '-',
            payment?.to ? payment?.to : '-',
          ])
        : [];

    const tempSimplePdfData = {
      title: 'ONBOARD',
      subtitle: 'Relatório de Recebimentos',
      filters: periodString || '',
      headers: headerSimplePdf,
      rows: rowsSimplePdf,
    };

    handleSetSimplePdfData(tempSimplePdfData);
  }, [initialPaymentsList, periodString]);

  return (
    <>
      {initialPaymentsList?.length! > 0 && (
        <Box width='100%' display='flex' justifyContent='flex-end'>
          <Suspense fallback='Carregando...'>
            <TotalCountComponent
              count={initialPaymentsList?.length! || 0}
              title='transações'
              position='right'
            />
          </Suspense>
        </Box>
      )}

      <TableContainer
        className={classes.tableContainer}
        {...events}
        ref={parentRef}>
        <Table className={classes.table} component={Paper}>
          <TableHead>
            <TableRow>
              <TableCell
                className={isHistoryDatails ? classes.firstTd : ''}
                variant='head'
                align='center'
                sortDirection={orderBy === 'value' ? order : false}>
                <TableSortLabel
                  active
                  direction={orderBy === 'value' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'value')}
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}>
                  Valor
                  {orderBy === 'value' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell
                variant='head'
                align='center'
                sortDirection={orderBy === 'date' ? order : false}>
                <TableSortLabel
                  active
                  direction={orderBy === 'date' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'date')}
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}>
                  Data e Hora
                  {orderBy === 'date' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell
                variant='head'
                align='center'
                sortDirection={orderBy === 'type' ? order : false}>
                <TableSortLabel
                  active
                  direction={orderBy === 'type' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'type')}
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}>
                  Tipo
                  {orderBy === 'type' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell
                variant='head'
                align='center'
                sortDirection={orderBy === 'from' ? order : false}>
                <TableSortLabel
                  active
                  direction={orderBy === 'from' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'from')}
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}>
                  De
                  {orderBy === 'from' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell
                variant='head'
                align='center'
                sortDirection={orderBy === 'to' ? order : false}>
                <TableSortLabel
                  active
                  direction={orderBy === 'to' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'to')}
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}>
                  Para
                  {orderBy === 'to' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              {transportOperatorSelected.type === 1 && (
                <TableCell
                  variant='head'
                  align='left'
                  sortDirection={
                    orderBy === 'transportOperatorId' ? order : false
                  }>
                  <TableSortLabel
                    classes={{
                      root: classes.sortLabel,
                      icon: classes.sortLabel,
                    }}
                    active
                    direction={
                      orderBy === 'transportOperatorId' ? order : 'asc'
                    }
                    onClick={(event) =>
                      handleRequestSort(event, 'transportOperatorId')
                    }>
                    Operação
                    {orderBy === 'transportOperatorId' ? (
                      <span className={classes.visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {initialPaymentsList.length > 0 &&
              stableSort(
                initialPaymentsList,
                getComparator(order, orderBy),
              ).map((payment: PaymentInterface) => (
                <TableRow key={payment.id}>
                  <TableCell align='center'>
                    {`R$ ${(payment.value || '0').toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                    })}`}
                  </TableCell>
                  <TableCell align='center'>
                    {new Date(payment?.date).getTime()
                      ? new Date(payment.date).toLocaleString('pt-BR')
                      : '---'}
                  </TableCell>
                  <TableCell align='center'>
                    {CONST_PAYMENT.TYPES.find(
                      ({ KEY }) => KEY === payment?.type,
                    )?.VALUE || '---'}
                  </TableCell>
                  <TableCell align='center'>{payment?.from || '---'}</TableCell>
                  <TableCell align='center'>{payment?.to || '---'}</TableCell>
                  {transportOperatorSelected.type === 1 && (
                    <TableCell>
                      {transportOperatorsList.find(
                        (e) => e.id === payment?.transportOperatorId,
                      )?.name || '---'}
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export { TablePayments };
export default TablePayments;
